import { Mic } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { VoiceNoteMeta } from "../aui/convert";
import { threadFileUrl } from "./files";
import { useThreadId } from "./useThreadId";

/** A length as a voice note shows it: "0:07", "2:30". */
export function clockLength(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

// One link per voice note, shared by every mount of it (a message scrolled back into view, a
// thread opened again): fetched once, and again only when it has expired.
const links = new Map<string, Promise<string | null>>();

function linkFor(threadId: number, id: number, fresh: boolean): Promise<string | null> {
  const key = `${threadId}:${id}`;
  const known = fresh ? undefined : links.get(key);
  if (known) return known;
  const link = threadFileUrl(threadId, id);
  links.set(key, link);
  void link.then((url) => {
    if (url == null && links.get(key) === link) links.delete(key); // none given: ask next time
  });
  return link;
}

/**
 * A voice note the user sent on a messaging channel: its length, its sound to play, and what it
 * says (what the assistant read). The sound comes from a short-lived link, fetched once per note
 * and once more if it has expired by the time it plays (playing on, if play was pressed); nothing
 * loads until it is played.
 */
export function VoiceNote({ note }: { note: VoiceNoteMeta }) {
  const threadId = useThreadId();
  const [url, setUrl] = useState<string | null>(null);
  const [expired, setExpired] = useState<string | null>(null); // the link that failed to load
  const audio = useRef<HTMLAudioElement>(null);
  const pressed = useRef(false); // the user pressed play

  useEffect(() => {
    if (threadId == null) return;
    let live = true;
    void linkFor(threadId, note.id, expired != null).then((next) => {
      if (live) setUrl(next);
    });
    return () => {
      live = false;
    };
  }, [threadId, note.id, expired]);

  useEffect(() => {
    if (expired && url && url !== expired && pressed.current) {
      void audio.current?.play().catch(() => {}); // the browser may still refuse: the controls stay
    }
  }, [expired, url]);

  const length = clockLength(note.seconds);
  return (
    <figure className="flex w-72 max-w-full flex-col gap-1.5">
      <p className="flex items-center gap-1.5 text-xs text-accent-foreground">
        <Mic className="h-3.5 w-3.5" aria-hidden />
        Voice note · {length}
      </p>
      <audio
        ref={audio}
        controls
        preload="none"
        src={url ?? undefined}
        aria-label={`Voice note, ${length}`}
        className="h-9 w-full"
        onPlay={() => {
          pressed.current = true;
        }}
        onEnded={() => {
          pressed.current = false;
        }}
        onError={() => {
          if (url && expired == null) setExpired(url);
        }}
      />
      <figcaption className="whitespace-pre-wrap break-words">{note.transcript}</figcaption>
    </figure>
  );
}
